import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

const PASSWORD_RECOVERY_FLAG_KEY = 'password_recovery_in_progress';
const PASSWORD_RESET_REQUEST_TYPE_KEY = 'password_reset_requested_type';
const PASSWORD_RESET_REQUEST_TYPE_TTL_MS = 60 * 60 * 1000;
const PASSWORD_RECOVERY_SESSION_TOKENS_KEY = 'password_recovery_session_tokens';

const storeRecoverySessionTokens = (session: any) => {
  if (!session?.access_token || !session?.refresh_token) return;
  try {
    sessionStorage.setItem(
      PASSWORD_RECOVERY_SESSION_TOKENS_KEY,
      JSON.stringify({
        accessToken: session.access_token,
        refreshToken: session.refresh_token,
        ts: Date.now(),
      })
    );
  } catch {
    // no-op
  }
};

const getRequestedResetUserType = (): 'client' | 'practitioner' | null => {
  // 1) Try localStorage / sessionStorage (same-origin only).
  try {
    const raw = localStorage.getItem(PASSWORD_RESET_REQUEST_TYPE_KEY)
      || sessionStorage.getItem(PASSWORD_RESET_REQUEST_TYPE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { userType?: string; ts?: number };
      const isFresh = !!parsed.ts && Date.now() - parsed.ts < PASSWORD_RESET_REQUEST_TYPE_TTL_MS;
      if (isFresh) {
        return parsed.userType === 'practitioner' ? 'practitioner' : 'client';
      }
      localStorage.removeItem(PASSWORD_RESET_REQUEST_TYPE_KEY);
      sessionStorage.removeItem(PASSWORD_RESET_REQUEST_TYPE_KEY);
    }
  } catch {
    localStorage.removeItem(PASSWORD_RESET_REQUEST_TYPE_KEY);
    sessionStorage.removeItem(PASSWORD_RESET_REQUEST_TYPE_KEY);
  }

  // 2) Fallback: cross-subdomain cookie (survives resonow.org → www.resonow.org redirect).
  try {
    const match = document.cookie.match(/(?:^|;\s*)password_reset_type=(client|practitioner)/);
    if (match) return match[1] as 'client' | 'practitioner';
  } catch { /* ignore */ }

  return null;
};

const AuthHashRedirect = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event !== 'PASSWORD_RECOVERY') return;

      // Prefer the type from the current URL query (set by resetPasswordForEmail redirect)
      // over user_metadata, because the user may hold both client & practitioner profiles.
      const urlType = new URLSearchParams(window.location.search).get('type');
      const requestedType = getRequestedResetUserType();
      const rawType = requestedType || urlType || session?.user?.user_metadata?.user_type;
      const userType = rawType === 'practitioner' ? 'practitioner' : 'client';
      storeRecoverySessionTokens(session);
      sessionStorage.setItem(
        PASSWORD_RECOVERY_FLAG_KEY,
        JSON.stringify({ ts: Date.now(), userType })
      );

      // Recovery should not look like a normal logged-in session.
      // Keep tokens in sessionStorage for reset flow, then clear local persisted auth.
      try {
        await supabase.auth.signOut({ scope: 'local' });
      } catch {
        // no-op
      }

      navigate(`/reset-password?type=${userType}`, { replace: true });
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  useEffect(() => {
    // Only handle accidental landing on homepage with auth hash.
    if (location.pathname !== '/') return;
    if (!location.hash) return;

    const hashParams = new URLSearchParams(location.hash.substring(1));
    const hashType = hashParams.get('type');
    const hasRecoveryTokens =
      hashType === 'recovery' &&
      !!hashParams.get('access_token') &&
      !!hashParams.get('refresh_token');

    const errorCode = hashParams.get('error_code') || '';
    const errorDescription = decodeURIComponent(hashParams.get('error_description') || '');
    const looksLikeRecoveryLinkError =
      /otp_expired|access_denied/i.test(errorCode) &&
      /link|expired|invalid/i.test(errorDescription);

    if (!hasRecoveryTokens && !looksLikeRecoveryLinkError) return;

    const currentQuery = new URLSearchParams(location.search);
    const typeParam = currentQuery.get('type');
    const requestedType = getRequestedResetUserType();
    const resolvedType = requestedType === 'practitioner'
      ? 'practitioner'
      : typeParam === 'practitioner'
        ? 'practitioner'
        : 'client';
    const userTypeQuery = resolvedType === 'practitioner' ? '?type=practitioner' : '?type=client';
    sessionStorage.setItem(
      PASSWORD_RECOVERY_FLAG_KEY,
      JSON.stringify({ ts: Date.now(), userType: resolvedType })
    );

    if (hasRecoveryTokens) {
      try {
        sessionStorage.setItem(
          PASSWORD_RECOVERY_SESSION_TOKENS_KEY,
          JSON.stringify({
            accessToken: hashParams.get('access_token'),
            refreshToken: hashParams.get('refresh_token'),
            ts: Date.now(),
          })
        );
      } catch {
        // no-op
      }
    }

    navigate(`/reset-password${userTypeQuery}${location.hash}`, { replace: true });
  }, [location.pathname, location.search, location.hash, navigate]);

  return null;
};

export default AuthHashRedirect;
